import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, memo, useMemo } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { BatteryChargingFull, Power, PowerOff, Usb } from '@mui/icons-material';
import { TypographyWithCopy } from 'console/shared/components/fields';
import { PowerSource } from 'console/device/device_ts';
import { LegacyDevice } from 'console/device/device';
import { Capability } from 'console/device/device-capability';
import { prettyFloat } from 'webui/utils/number';
import { ResponsiveIcon } from '../../icon/responsive';
import Voltage from './icons/voltage.svg';
import { useHelpPopup } from 'shared/hooks/ui/help-popup';
import { isVehicleDevice } from 'console/shared/contexts/device/config/utils';
function powerState(power_source, is_vehicle) {
    switch (power_source) {
        case PowerSource.battery:
            return 'Аккумулятор';
        case PowerSource.main:
            return is_vehicle ? 'Бортовая сеть' : 'Сеть';
        case PowerSource.usb:
            return 'USB';
        default:
            return 'Неизвестен';
    }
}
const IconElement = memo(forwardRef((props, ref) => {
    const { power_source, is_vehicle, ...rest } = props;
    switch (power_source) {
        case PowerSource.battery:
            return (_jsx(ResponsiveIcon, { Icon: BatteryChargingFull, ...rest, ref: ref }));
        case PowerSource.main:
            return (_jsx(ResponsiveIcon, { Icon: !is_vehicle ? Power : Voltage, large: is_vehicle, ...rest, ref: ref }));
        case PowerSource.usb:
            return _jsx(ResponsiveIcon, { Icon: Usb, ...rest, ref: ref });
        default:
            return _jsx(ResponsiveIcon, { Icon: PowerOff, ...rest, ref: ref });
    }
}));
IconElement.displayName = 'IconElement';
export const PowerIcon = memo(function PowerIcon(props) {
    const { capabilities, power_source, device_type, voltage, online, ...rest } = props;
    const has_lithium_battery = LegacyDevice.has_any_capability(capabilities, [
        Capability.has_lithium_battery,
        Capability.has_lithium_ztc,
    ]);
    const is_vehicle = device_type ? isVehicleDevice(device_type) : false;
    const ztc_absent_voltage = LegacyDevice.has_capability(capabilities, Capability.has_lithium_ztc) &&
        voltage != null &&
        voltage < 7;
    const _power_source = useMemo(() => {
        return power_source != null
            ? power_source
            : has_lithium_battery && voltage != null && voltage < 7
                ? PowerSource.battery
                : PowerSource.main;
    }, [power_source, has_lithium_battery, voltage]);
    const [HelpPopup, open] = useHelpPopup();
    if (!LegacyDevice.has_capability(capabilities, Capability.has_voltage_sensor)) {
        return null;
    }
    return (_jsxs(_Fragment, { children: [_jsxs(Box, { display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, position: "relative", onClick: open, children: [_jsx(IconElement, { power_source: _power_source, color: !online
                            ? 'disabled'
                            : _power_source == PowerSource.battery
                                ? 'error'
                                : 'action', is_vehicle: is_vehicle, ...rest }), is_vehicle && (_jsx(Typography, { top: "57%", position: "absolute", left: "50%", sx: {
                            transform: 'translate3d(-50%, -50%, 0)',
                            userSelect: 'none',
                        }, color: "textSecondary", fontSize: "0.35em", children: `${prettyFloat(voltage, 1)}` }))] }), _jsx(HelpPopup, { children: _jsxs(Box, { width: "100%", children: [!online && (_jsxs(_Fragment, { children: [_jsx(Typography, { color: "error", sx: { mb: 1 }, children: "\u0423\u0441\u0442\u0440\u043E\u0439\u0441\u0442\u0432\u043E \u043D\u0435 \u043D\u0430 \u0441\u0432\u044F\u0437\u0438" }), _jsx(Typography, { color: "textSecondary", children: "\u041F\u043E\u043A\u0430\u0437\u0430\u043D\u044B \u043F\u043E\u0441\u043B\u0435\u0434\u043D\u0438\u0435 \u0438\u0437\u0432\u0435\u0441\u0442\u043D\u044B\u0435 \u0434\u0430\u043D\u043D\u044B\u0435" }), _jsx(Divider, {})] })), _jsx(TypographyWithCopy, { label: "\u0418\u0441\u0442\u043E\u0447\u043D\u0438\u043A", value: powerState(_power_source, is_vehicle), sx: { mb: 2 } }), _jsx(TypographyWithCopy, { label: "\u041D\u0430\u043F\u0440\u044F\u0436\u0435\u043D\u0438\u0435", value: voltage != null
                                ? ztc_absent_voltage
                                    ? 'отсутствует'
                                    : `${prettyFloat(voltage, 1)} В`
                                : 'неизвестно', error: LegacyDevice.has_capability(capabilities, Capability.has_voltage_12v_low_level) &&
                                voltage != null &&
                                voltage < 12 })] }) })] }));
});
